import { useState } from 'react'

import { Text } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Delete, Replace } from '@factorialco/factorial-one-react/icons/app'
import {
  BulkUpdateTasksDocument,
  MyProjectsOverviewDocument,
  PendingProjectTasksDataCollectionDocument,
  PendingProjectTasksDataCollectionItemFragment,
  ProjectsAssignedDataCollectionDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { isValidTaskStatus } from 'modules/tasks/lib/status'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { getStatusColor, getStatusLabel } from '../../labelTags/TaskStatusTag'
import { DeleteModal } from '../ProjectTasksDataCollection/modals/DeleteModal'

interface IPendingProjectTasksDataCollectionProps {
  assigneeId: number
  projectId?: number
  projectName?: string
}

let totalPendingTasks = 0
const Header = ({ projectName }: { projectName?: string }) => {
  return projectName ? (
    <Text.Header2 weight="semibold">
      {i18n.t('project_management.my_projects.overview.pending_tasks.description')}
      {`${projectName} (${totalPendingTasks})`}
    </Text.Header2>
  ) : (
    <Text.Header2 weight="semibold">
      {i18n.t('project_management.my_projects.overview.pending_tasks.description')}
      {totalPendingTasks}
    </Text.Header2>
  )
}

const PendingProjectTasksDataCollection = ({
  assigneeId,
  projectId,
  projectName,
}: IPendingProjectTasksDataCollectionProps) => {
  const { navigation } = useNavigation()

  const [openModal, setOpenModal] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const onCloseModal = () => setOpenModal(false)

  const [bulkUpdateTasks, { loading: bulkUpdateTasksLoading }] = useMutation(
    BulkUpdateTasksDocument,
    {
      extractorFn: (data) => data.tasksMutations.bulkUpdateTasksTask,
    }
  )

  const getRefetchQueries = (status?: string, isForDeleteModal?: boolean) => {
    if (isForDeleteModal || status === 'done' || status === 'discarded') {
      return [
        MyProjectsOverviewDocument,
        PendingProjectTasksDataCollectionDocument,
        ProjectsAssignedDataCollectionDocument,
      ]
    }

    return [PendingProjectTasksDataCollectionDocument]
  }

  const source = useGraphqlDataSource({
    document: PendingProjectTasksDataCollectionDocument,
    mapData: (data) => data.projectManagement?.projectTasksConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      assigneeId,
      projectIds: projectId ? [projectId] : [],
      search,
      sortOrder: sortings?.[0],
    }),
    itemUrl: (item) =>
      `${navigation.tasks.taskIdAndIdentifier.fullPath({
        taskId: Number(item.task?.id),
        identifier: TaskIdentifier.Project,
      })}`,
    mapPagination: (data) => {
      totalPendingTasks = data.projectManagement.projectTasksConnection.totalCount ?? 0
      return {
        total: totalPendingTasks,
      }
    },
    selectable: (item) => item.task?.id,
    bulkActions: () => {
      return {
        primary: [
          {
            label: i18n.t('tasks.status.change_status'),
            id: 'change-status',
            icon: Replace,
            loading: bulkUpdateTasksLoading,
          },
          {
            label: i18n.t('tasks.status.todo'),
            id: 'change-status;todo',
          },
          {
            label: i18n.t('tasks.status.done'),
            id: 'change-status;done',
          },
          {
            label: i18n.t('tasks.status.in_progress'),
            id: 'change-status;in_progress',
          },
          {
            label: i18n.t('tasks.status.discarded'),
            id: 'change-status;discarded',
          },
        ],
        secondary: [
          {
            label: i18n.t('common.delete'),
            icon: Delete,
            critical: true,
            id: 'delete',
          },
        ],
      }
    },
    search: {
      enabled: true,
    },
  })

  return (
    <>
      <Header projectName={projectName} />
      <OneDataCollection
        source={source}
        onBulkAction={async (action, selectedItems) => {
          if (action.startsWith('change-status')) {
            const [, status] = action.split(';')

            if (status && isValidTaskStatus(status)) {
              const result = await bulkUpdateTasks({
                variables: {
                  tasks: selectedItems.itemsStatus.map((item) => ({
                    id: item.item.task?.id ?? 0,
                    status,
                    identifier: TaskIdentifier.Project,
                  })),
                },
                refetchQueries: getRefetchQueries(status),
              })

              const handled = handleResult(result, (_) => {})
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('common.saved_successfully'),
                })
              }
            }
          } else if (action === 'delete') {
            setOpenModal(true)
            setSelectedIds(selectedItems.itemsStatus.map((item) => item.item.id))
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('project_management.table.tasks.headers.name'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.task?.name,
                  }),
                },
                {
                  label: i18n.t('tasks.labels.belongs_to'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.task?.relatedResourceTitle ?? undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.assignees'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'avatarList',
                    value: {
                      avatarList:
                        projectTask.task?.assigneesConnection.nodes.map((node) => ({
                          type: 'person',
                          firstName: node.firstName,
                          lastName: node.lastName,
                          src: node.avatar ? buildUrl(node.avatar) : undefined,
                        })) ?? [],
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.status'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      status: projectTask.task?.status
                        ? getStatusColor(projectTask.task.status)
                        : 'neutral',
                      label: projectTask.task?.status
                        ? getStatusLabel(projectTask.task.status)
                        : '-',
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.starts_on'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.startsOn
                      ? new Date(projectTask.task?.startsOn)
                      : undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.due_on'),
                  render: (projectTask: PendingProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.dueOn ? new Date(projectTask.task?.dueOn) : undefined,
                  }),
                },
              ],
            },
          },
        ]}
      />
      {openModal && (
        <DeleteModal
          ids={selectedIds}
          onCloseModal={onCloseModal}
          projectName={projectName ?? ''}
          refetchQueries={getRefetchQueries(undefined, true)}
        />
      )}
    </>
  )
}

export default PendingProjectTasksDataCollection
